/**
 * Auth + tenant headers shared by the axios interceptor (`config/axios.ts`)
 * and the SSE fetch in `aiChatApi.ts`. Reads localStorage directly because
 * axios is imported before Pinia is initialized — the original axios setup
 * had to do the same dance, this just centralizes it so non-axios callers
 * get identical behavior.
 */
import { CENTRAL_SITE_MARKER, UserRole } from '@hms/shared';

/** `X-Hospital-Id` value of the "All hospitals" scope. */
export const ALL_HOSPITALS_MARKER = 'ALL';

// Roles that may send `X-Hospital-Id: ALL` — kept in sync with the backend
// ActiveHospitalGuard.
const ROLES_WITH_ALL_ACCESS: ReadonlySet<string> = new Set([UserRole.ADMIN, UserRole.SUPER_ADMIN]);

interface PersistedHospitalContext {
  activeHospitalId: string | null;
  centralSiteSelected?: boolean;
}

interface PersistedAuth {
  token?: string;
  refreshToken?: string;
  user?: { role?: string };
}

function readJson<T>(key: string): T | null {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function readAuthContext(): PersistedAuth | null {
  return readJson<PersistedAuth>('auth');
}

export function readHospitalContext(): PersistedHospitalContext | null {
  return readJson<PersistedHospitalContext>('hospitalContext');
}

/**
 * The one rule for being on the central site, shared by these headers and the hospital-context store: always for the
 * central stock manager, which has no other site; for an org admin once it has picked "Central warehouses"; never for
 * any other role, whatever the stored selection says.
 */
export const isOnCentralSite = (role: string | undefined, centralSiteSelected: boolean): boolean =>
  role === UserRole.CENTRAL_STOCK_MANAGER || (role === UserRole.ADMIN && centralSiteSelected);

/**
 * Returns the `Authorization` and `X-Hospital-Id` headers an authenticated
 * request needs. Empty object when there's no token (anonymous request).
 */
export function buildAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const auth = readAuthContext();
  if (!auth?.token) return headers;

  headers.Authorization = `Bearer ${auth.token}`;

  const role = auth.user?.role;
  if (!role || role === UserRole.PATIENT) return headers;

  const hospitalContext = readHospitalContext();
  if (isOnCentralSite(role, hospitalContext?.centralSiteSelected === true)) {
    headers['X-Hospital-Id'] = CENTRAL_SITE_MARKER;
    return headers;
  }

  const activeId = hospitalContext?.activeHospitalId ?? null;
  if (activeId) {
    headers['X-Hospital-Id'] = activeId;
  } else if (ROLES_WITH_ALL_ACCESS.has(role)) {
    headers['X-Hospital-Id'] = ALL_HOSPITALS_MARKER;
  }
  // Else leave the header absent — backend will 400 with a clear message.
  return headers;
}
